import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Crown, MapPin, PlusCircle, ArrowRight, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, STATUS_LABEL } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";

export default function Family() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", address: "", bio: "" });
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/family/members"); setMembers(r.data); } catch {} };
  useEffect(() => { load(); }, []);

  const isFamily = user?.plan === "family";

  const addMember = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/family/members", { ...form, lat: coords.lat, lng: coords.lng });
      toast.success(`${form.name} added to your family circle`);
      setForm({ name: "", address: "", bio: "" }); setCoords({ lat: null, lng: null });
      load();
    } catch (e) {
      const msg = apiError(e);
      toast.error(msg);
      if (e?.response?.status === 402) navigate("/app/plans");
    } finally { setBusy(false); }
  };

  const useLoc = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("Location set"); });
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-gold-light border-2 border-gold flex items-center justify-center">
          <Crown size={26} strokeWidth={2.5} className="text-terracotta" />
        </div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900">Connect Family</h1>
      </div>
      <p className="text-lg text-forest-700 mb-6 max-w-2xl">Manage a loved one's profile and arrange help on their behalf — perfect for looking after a parent who lives on their own.</p>

      {!isFamily && (
        <div className="bg-forest-900 text-white rounded-2xl p-6 mb-6 flex flex-wrap items-center gap-4" data-testid="family-upsell">
          <div>
            <p className="font-head text-xl font-bold">Connect Family is required</p>
            <p className="text-cream/80">Upgrade to add and manage family members. (Demo — no charge.)</p>
          </div>
          <button onClick={() => navigate("/app/plans")} data-testid="go-plans"
            className="ml-auto min-h-[52px] px-6 rounded-full bg-terracotta text-white font-bold hover:bg-terracotta-dark transition-colors flex items-center gap-2">
            See plans <ArrowRight size={20} strokeWidth={2.5} />
          </button>
        </div>
      )}

      <form onSubmit={addMember} className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8 mb-6">
        <h2 className="font-head text-2xl font-bold text-forest-900 mb-4 flex items-center gap-2"><UserPlus size={24} strokeWidth={2.5} className="text-terracotta" /> Add a family member</h2>
        <div className="space-y-4">
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">Their name</label>
            <input data-testid="family-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Mom (Nirmal)" className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
          </div>
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">Their neighbourhood</label>
            <input data-testid="family-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="e.g. Edmond, OK" className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
            <button type="button" onClick={useLoc} data-testid="family-location"
              className={`mt-3 min-h-[48px] px-5 rounded-full font-semibold border-2 flex items-center gap-2 ${coords.lat != null ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-border"}`}>
              <MapPin size={18} strokeWidth={2.5} /> {coords.lat != null ? "Location set ✓" : "Set their location"}
            </button>
          </div>
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">About them (optional)</label>
            <textarea data-testid="family-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={2}
              placeholder="A little about them and how the community can help" className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream resize-none" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="family-submit"
          className="w-full min-h-[56px] mt-5 rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60">
          {busy ? "Adding…" : "Add family member"}
        </button>
      </form>

      {members.length > 0 && (
        <div className="space-y-4" data-testid="family-list">
          {members.map((m) => <FamilyCard key={m.user_id} member={m} navigate={navigate} />)}
        </div>
      )}
    </div>
  );
}

function FamilyCard({ member, navigate }) {
  const [open, setOpen] = useState(false);
  const [overview, setOverview] = useState(null);

  const toggle = async () => {
    setOpen((v) => !v);
    if (!overview) {
      try { const r = await api.get(`/family/members/${member.user_id}/overview`); setOverview(r.data); } catch {}
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-5" data-testid={`family-card-${member.user_id}`}>
      <div className="flex items-center gap-3">
        <Avatar user={member} size={52} />
        <div className="min-w-0">
          <p className="font-bold text-forest-900 text-lg">{member.name}</p>
          {member.address && <p className="text-forest-500 flex items-center gap-1"><MapPin size={14} strokeWidth={2.5} />{member.address}</p>}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => navigate(`/app/new?for=${member.user_id}`)} data-testid={`family-post-${member.user_id}`}
            className="min-h-[48px] px-4 rounded-full bg-terracotta text-white font-semibold flex items-center gap-1.5 hover:bg-terracotta-dark transition-colors">
            <PlusCircle size={18} strokeWidth={2.5} /> Request help
          </button>
          <button onClick={toggle} data-testid={`family-view-${member.user_id}`}
            className="min-h-[48px] px-4 rounded-full border-2 border-border text-forest-700 font-semibold hover:border-forest-500 transition-colors flex items-center gap-1.5">
            <ClipboardList size={18} strokeWidth={2.5} /> {open ? "Hide" : "Activity"}
          </button>
        </div>
      </div>
      {open && overview && (
        <div className="mt-4 pt-4 border-t border-border space-y-2">
          <p className="font-semibold text-forest-900">Posts ({overview.posts.length}) · Connections ({overview.connections.length})</p>
          {overview.posts.slice(0, 5).map((p) => (
            <button key={p.post_id} onClick={() => navigate(`/app/post/${p.post_id}`)} className="w-full text-left p-3 rounded-xl bg-cream border border-border hover:border-forest-500 transition-colors">
              <span className="font-semibold text-forest-900">{p.title}</span>
              <span className="ml-2 text-sm text-forest-500">· {STATUS_LABEL[p.status]}</span>
            </button>
          ))}
          {overview.posts.length === 0 && <p className="text-forest-500">No requests yet. Tap “Request help” to post for them.</p>}
        </div>
      )}
    </div>
  );
}
